// Shared helpers for the Governance Health member UI.

export const RULE_GROUP_ORDER = [
  "foundation",
  "meetings",
  "annual_duties",
  "ongoing_record",
  "storage",
] as const;

export const RULE_GROUP_LABELS: Record<string, string> = {
  foundation: "Foundation",
  meetings: "Meetings",
  annual_duties: "Annual Duties",
  ongoing_record: "Ongoing Record",
  storage: "Storage",
};

export type ItemStatus =
  | "satisfied"
  | "overdue"
  | "due_soon"
  | "upcoming"
  | "not_applicable";

export const STATUS_LABEL: Record<ItemStatus, string> = {
  satisfied: "Satisfied",
  overdue: "Ready for your attention",
  due_soon: "Due soon",
  upcoming: "Upcoming",
  not_applicable: "Not applicable",
};

// Tailwind classes for status chips — warm, never alarmist.
export const STATUS_CLASS: Record<ItemStatus, string> = {
  satisfied: "bg-emerald-100 text-emerald-800 border-emerald-200",
  overdue: "bg-amber-100 text-amber-900 border-amber-300",
  due_soon: "bg-amber-50 text-amber-800 border-amber-200",
  upcoming: "bg-muted text-muted-foreground border-border",
  not_applicable: "bg-muted text-muted-foreground border-border",
};

// Rule → resolution_category mapping (matches DEFAULT_CATEGORIES in
// ResolutionCategorySelect). Fallback: "Governance".
export const RULE_TO_RESOLUTION_CATEGORY: Record<string, string> = {
  banking_authority_resolution: "Financial",
  cso_designated: "Appointment",
  successor_directive_executed: "Governance",
  successor_directive_review: "Governance",
  annual_oversight_review: "Governance",
  compliance_affirmation: "Governance",
};

export type RuleForPrefill = {
  rule_key: string;
  title: string | null;
  source_reference: string | null;
};

// Build a minimal, editable resolution skeleton from a compliance rule.
// Structural only — no doctrinal or theological language.
export function buildResolutionPrefill(
  rule: RuleForPrefill,
  itemId: string | null,
): Record<string, any> {
  const title = rule.title || "Governance Resolution";
  const category =
    RULE_TO_RESOLUTION_CATEGORY[rule.rule_key] || "Governance";
  const src = rule.source_reference || "the Constitution";
  const whereas = `${src} provides the basis for this action`;
  const resolved = `${title} is adopted and recorded in the governance record of the UNA`;
  return {
    resolution_title: title,
    resolution_category: category,
    whereas_clauses: [whereas],
    resolved_clauses: [resolved],
    gh_rule: rule.rule_key,
    gh_item: itemId || undefined,
  };
}

// Deep-link mapping. Resolution rules link into the writing_resolution
// launcher with query params so the flow prefills and reports satisfaction.
// Meeting rules link into the recording_minutes launcher.
export function getActionForRule(
  ruleKey: string,
  ruleGroup: string,
  itemId?: string,
  evidenceTypes?: string[] | null,
): {
  href: string;
  label: string;
} {
  if (ruleKey === "journey_complete") {
    return { href: "/journey", label: "Continue My Journey" };
  }
  if (
    ruleGroup === "meetings" ||
    ruleKey === "first_meeting_minuted" ||
    (evidenceTypes || []).includes("minutes")
  ) {
    const q = new URLSearchParams({ workflow: "recording_minutes" });
    if (itemId) {
      q.set("gh_rule", ruleKey);
      q.set("gh_item", itemId);
    }
    return { href: `/workflows?${q.toString()}`, label: "Record Meeting Minutes" };
  }
  if (
    (evidenceTypes || []).includes("resolution") ||
    ruleKey.endsWith("_resolution") ||
    ruleKey === "cso_designated" ||
    ruleKey === "successor_directive_executed" ||
    ruleKey === "successor_directive_review" ||
    ruleKey === "annual_oversight_review" ||
    ruleKey === "compliance_affirmation"
  ) {
    const q = new URLSearchParams({ workflow: "writing_resolution" });
    if (itemId) {
      q.set("gh_rule", ruleKey);
      q.set("gh_item", itemId);
    }
    return { href: `/workflows?${q.toString()}`, label: "Draft Resolution" };
  }
  // storage / upload / continuity / transfer → stay on Governance health tab
  return { href: "/governance/health", label: "Review Requirements" };
}

export function scoreStatusLine(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return "Your record is just getting started — the path is clear ahead.";
  }
  if (score >= 85) return "Your record is strong. Keep the small habits going.";
  if (score >= 60)
    return "You're on a steady footing. A few small steps will strengthen it further.";
  if (score >= 30)
    return "You've begun the record. Here's the clearest path to strengthen it.";
  return "Your record so far, and the clear path to strengthen it.";
}
